import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, BoxProps, IconButton, Stack, Typography } from "@mui/material";
import React, { ReactNode } from "react";
import { CHART_COLORS, CHART_DIMENSIONS, CHART_TYPOGRAPHY } from "./tokens";

interface DataCardColumn {
  width?: string | number;
}

interface DataCardRow {
  cells: ReactNode[];
  bg?: string;
}

export interface DataCardSection {
  sectionHeader?: string;
  rows: DataCardRow[];
}

interface DataCardProps {
  title: string;
  onEdit?: () => void;
  columns?: DataCardColumn[];
  sections: DataCardSection[];
  headerExtra?: ReactNode;
  hideHeader?: boolean;
  sx?: BoxProps["sx"];
}

export const CardHeader: React.FC<{
  title: string;
  onEdit?: () => void;
  headerExtra?: ReactNode;
}> = ({ title, onEdit, headerExtra }) => (
  <Box
    sx={{
      bgcolor: CHART_COLORS.surfaceHeader,
      borderBottom: `1px solid ${CHART_COLORS.border}`,
      borderRadius: `${CHART_DIMENSIONS.borderRadius} ${CHART_DIMENSIONS.borderRadius} 0 0`,
      px: headerExtra
        ? CHART_DIMENSIONS.cardHeaderPxLarge
        : CHART_DIMENSIONS.cardHeaderPx,
      py: headerExtra ? 0 : CHART_DIMENSIONS.cardHeaderPy,
      height: headerExtra ? CHART_DIMENSIONS.cardHeaderHeightLarge : undefined,
      minHeight: headerExtra ? undefined : CHART_DIMENSIONS.cardHeaderHeight,
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box display="flex" alignItems="center">
        <Typography
          sx={{
            ...CHART_TYPOGRAPHY.body5,
            fontWeight: CHART_TYPOGRAPHY.weight.medium,
            color: CHART_COLORS.textDark,
            fontFamily: CHART_TYPOGRAPHY.fontFamily,
          }}
        >
          {title}
        </Typography>
        {onEdit && (
          <IconButton
            size="small"
            onClick={onEdit}
            sx={{ p: "2px", ml: "4px" }}
            aria-label={`Edit ${title}`}
          >
            <EditOutlinedIcon
              sx={{
                fontSize: CHART_DIMENSIONS.iconSm,
                color: CHART_COLORS.textDark,
              }}
            />
          </IconButton>
        )}
      </Box>
    </Box>
    {headerExtra && <Box mt={0.5}>{headerExtra}</Box>}
  </Box>
);

const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <Box
    sx={{
      px: CHART_DIMENSIONS.cardHeaderPx,
      py: CHART_DIMENSIONS.cardRowPy,
      bgcolor: CHART_COLORS.surfaceWhite,
    }}
  >
    <Typography
      sx={{
        ...CHART_TYPOGRAPHY.body5,
        fontWeight: CHART_TYPOGRAPHY.weight.medium,
        color: CHART_COLORS.textDark,
        fontFamily: CHART_TYPOGRAPHY.fontFamily,
        "&::before": { content: '"\u2022 "' },
      }}
    >
      {label}
    </Typography>
  </Box>
);

const DataCard: React.FC<DataCardProps> = ({
  title,
  onEdit,
  columns,
  sections,
  headerExtra,
  hideHeader = false,
  sx,
}) => {
  let globalRowIndex = 0;

  return (
    <Box
      sx={{
        border: `1px solid ${CHART_COLORS.border}`,
        borderRadius: CHART_DIMENSIONS.borderRadius,
        overflow: "hidden",
        width: "100%",
        ...sx,
      }}
    >
      {!hideHeader && (
        <CardHeader title={title} onEdit={onEdit} headerExtra={headerExtra} />
      )}

      <Stack direction="column">
        {sections.map((section, sectionIndex) => (
          <React.Fragment key={sectionIndex}>
            {section.sectionHeader && (
              <>
                <Box
                  sx={{
                    height: "1px",
                    bgcolor: CHART_COLORS.border,
                  }}
                />
                <SectionHeader label={section.sectionHeader} />
              </>
            )}

            {section.rows.map((row, rowIndexInSection) => {
              const isOdd = globalRowIndex % 2 !== 0;
              const rowBg =
                row.bg ??
                (isOdd ? CHART_COLORS.surfaceAlt : CHART_COLORS.surfaceWhite);
              globalRowIndex++;

              return (
                <Box
                  key={`${sectionIndex}-${rowIndexInSection}`}
                  display="flex"
                  flexDirection="row"
                  sx={{ bgcolor: rowBg }}
                >
                  {row.cells.map((cell, cellIndex) => {
                    const colDef = columns?.[cellIndex];
                    // const isLast = cellIndex === row.cells.length - 1;

                    return (
                      <Box
                        key={cellIndex}
                        sx={{
                          flex: colDef?.width ? `0 0 ${colDef.width}` : 1,
                          // borderRight: isLast
                          //   ? "none"
                          //   : `1px solid ${CHART_COLORS.border}`,
                          px: CHART_DIMENSIONS.cardRowPx,
                          py: CHART_DIMENSIONS.cardRowPy,
                          minHeight: CHART_DIMENSIONS.cardRowMinHeight,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {cell}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Stack>
    </Box>
  );
};

export default DataCard;
